import { API } from "@swivl-mono/api-helper";
import { parseFreeformHours } from "@swivl-mono/helpers";
import { LocationVarKey, ScheduleSlug } from "@swivl-mono/types";
import { Spinner } from "@swivl-mono/ui";
import { useEffect, useState } from "react";
import { FiMapPin } from "react-icons/fi";

import { useLocationVariables } from "../../../../Hooks/useLocationVariables";
import { Member } from "../../../../Models/Member/Member.Current";
import { LocationTimezoneSection } from "../../CortexV3/Components/ScheduleEditor/LocationTimezoneSection";
import { ScheduleSlugSection } from "../../CortexV3/Components/ScheduleEditor/ScheduleSlugSection";
import { PageScope } from "../../CortexV3/Components/ScheduleEditor/Scope";
import { StepOutcome, WizardFooter, WizardStepShell } from "./WizardChrome";

interface HoursStepProps {
  locationId: number;
  groupId: number;
  sourceId: number | null;
  onBack?: () => void;
  onClose: () => void;
  onNext: (outcome: StepOutcome) => void;
}

// Each scrapeable weekly schedule: the type on the scrape, its slug, and its label.
// ScheduleSlugSection requires a caption; left blank here since ScheduleCard only renders it when non-empty.
const WEEKLY: { type: "officeHours" | "gateAccess" | "callCenter"; slug: ScheduleSlug; label: string }[] = [
  { type: "officeHours", slug: "office-hours", label: "Office Hours" },
  { type: "gateAccess", slug: "gate-access", label: "Gate Access" },
  { type: "callCenter", slug: "call-center", label: "Call Center Hours" }
];

type Citations = Partial<Record<"officeHours" | "gateAccess" | "callCenter" | "holidays", string>>;

// Timezone + hours; applies each schedule's scraped free-text on every open, overwriting prior edits.
export function HoursStep({ locationId, groupId, sourceId, onBack, onClose, onNext }: HoursStepProps): React.ReactElement {
  const scope: PageScope = { kind: "location", groupId, locationId };
  const vars = useLocationVariables<LocationVarKey.CortexFacilityCity | LocationVarKey.CortexFacilityState>(
    [LocationVarKey.CortexFacilityCity, LocationVarKey.CortexFacilityState],
    undefined,
    locationId
  );
  const place = [vars?.[LocationVarKey.CortexFacilityCity], vars?.[LocationVarKey.CortexFacilityState]].filter(Boolean).join(", ");
  const [ready, setReady] = useState(false);
  const [citations, setCitations] = useState<Citations>({});
  const [timezoneValue, setTimezoneValue] = useState("");
  const [timezoneConfirmed, setTimezoneConfirmed] = useState(false);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    const init = async (): Promise<void> => {
      try {
        if (sourceId == null) return;
        const row = await API.DocumentIngest.getUpload(Member.orgId, sourceId);
        const hours = row?.structuredFindings?.hours;
        if (!hours) return;
        setCitations({
          officeHours: hours.officeHours?.citation,
          gateAccess: hours.gateAccess?.citation,
          callCenter: hours.callCenter?.citation,
          holidays: hours.holidays?.citation
        });
        for (const { type, slug, label } of WEEKLY) {
          const text = hours[type]?.text;
          if (!text) continue;
          const weekly = parseFreeformHours(text);
          if (weekly && weekly.length > 0) {
            await API.Schedule.upsertSchedule(Member.orgId, { locationId, groupId: null, slug, label, weekly, isActive: true });
          }
        }
      } finally {
        setReady(true);
      }
    };
    void init();
  }, [sourceId, locationId]);

  if (!ready) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // Scraped hours are the evidence behind the prefill — a boxed callout, not fine print.
  const citation = (text?: string): React.ReactElement | null =>
    text ? (
      <div className="rounded-md border-l-2 border-orange-400 bg-grey-50 px-3 py-2">
        <p className="body-2xs font-semibold tracking-wide text-grey-400 uppercase">From the website</p>
        <p className="body-sm text-grey-700 italic">“{text}”</p>
      </div>
    ) : null;

  // Confirms an unedited timezone before approving, then saves it like an edit would.
  const handleApprove = async (): Promise<void> => {
    if (timezoneConfirmed) {
      onNext("completed");
      return;
    }
    if (
      !window.confirm(
        `Confirm the ${timezoneValue || "not set"} timezone is correct for ${place || "this location"}. Press Cancel to edit and OK to approve.`
      )
    )
      return;
    setApproving(true);
    try {
      await API.LocationVars.createVariable(LocationVarKey.CortexFacilityTimezone, timezoneValue, Member.orgId, undefined, locationId);
      onNext("completed");
    } catch (err) {
      window.alert(`Couldn't save the timezone: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setApproving(false);
    }
  };

  return (
    <WizardStepShell
      footer={
        <WizardFooter onBack={onBack} onClose={onClose} onSkip={() => onNext("skipped")} onApprove={() => void handleApprove()} saving={approving} />
      }
    >
      <div className="flex flex-col gap-1.5">
        {place && (
          <span className="body-sm flex items-center gap-1.5 text-grey-700">
            <FiMapPin className="size-4 text-grey-400" /> {place}
          </span>
        )}
        <LocationTimezoneSection scope={scope} onValueChange={setTimezoneValue} onSaved={() => setTimezoneConfirmed(true)} />
      </div>
      {WEEKLY.map(({ type, slug, label }) => (
        <div key={slug} className="flex flex-col gap-1">
          {citation(citations[type])}
          <ScheduleSlugSection slug={slug} label={label} caption="" scope={scope} />
        </div>
      ))}
      {citations.holidays && (
        <div className="flex flex-col gap-1">
          {citation(citations.holidays)}
          <p className="body-xs text-grey-500">Holiday hours are date-based — add them in the Holidays section on the location.</p>
        </div>
      )}
    </WizardStepShell>
  );
}
